import { useEffect } from 'react';
import { useMutation } from '@tanstack/react-query';
import { verifyEmail } from '../lib/api';
import { useDocumentTitle } from '../hooks/useDocumentTitle';

const VERIFY_PATH_PREFIX = '/verify/';

export function extractTokenFromPath(pathname: string): string | null {
  if (!pathname.startsWith(VERIFY_PATH_PREFIX)) return null;
  const raw = pathname.slice(VERIFY_PATH_PREFIX.length);
  if (!raw) return null;
  // Strip any trailing slash + accept the first path segment only
  const segment = raw.split('/')[0];
  if (!segment) return null;
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

export function VerifyEmailHandler() {
  const token = extractTokenFromPath(window.location.pathname);
  useDocumentTitle('Verify email');

  const mutation = useMutation({
    mutationFn: verifyEmail,
  });

  useEffect(() => {
    if (token) mutation.mutate(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (!token) {
    return (
      <div className="verify-email-pane">
        <div className="verify-email-card" role="alert">
          <h1>Missing verification token</h1>
          <p>
            This page expects a token in the URL (like
            <code> /verify/abc123</code>). Re-open the link from your email.
          </p>
          <a href="/" className="login-form-submit">
            Back to Sign in
          </a>
        </div>
      </div>
    );
  }

  // mutation.status starts as 'idle' before the useEffect fires `mutate`;
  // treat both idle + pending as the loading state so the first paint isn't
  // a flash of the failure UI.
  if (mutation.isPending || mutation.isIdle) {
    return (
      <div className="verify-email-pane">
        <div className="verify-email-card" role="status" aria-live="polite">
          <h1>Verifying your email…</h1>
          <p>Hold tight — this should only take a moment.</p>
        </div>
      </div>
    );
  }

  if (mutation.isError) {
    return (
      <div className="verify-email-pane">
        <div className="verify-email-card" role="alert">
          <h1>Verification failed</h1>
          <p>
            We couldn't reach the server to verify your email. Check your
            connection and try again.
          </p>
          <button
            type="button"
            className="login-form-submit"
            onClick={() => mutation.mutate(token)}
          >
            Try again
          </button>
        </div>
      </div>
    );
  }

  const verified = mutation.data?.verified === true;

  if (verified) {
    return (
      <div className="verify-email-pane">
        <div className="verify-email-card verify-email-card-success" role="status">
          <h1>Email verified</h1>
          <p>
            Your account is fully set up. You can now sign in to the operator
            console.
          </p>
          <a href="/" className="login-form-submit">
            Sign in
          </a>
        </div>
      </div>
    );
  }

  // Server returned 200 OK + `verified: false` — per F.13 anti-enumeration,
  // every non-fatal outcome (unknown / expired / consumed / mismatched) is
  // indistinguishable. Show a generic message.
  return (
    <div className="verify-email-pane">
      <div className="verify-email-card" role="alert">
        <h1>Link can't be verified</h1>
        <p>
          This verification link is invalid, expired, or already used. Sign in
          (if your account is active), or sign up again to request a new link.
        </p>
        <a href="/" className="login-form-submit">
          Back to Sign in
        </a>
      </div>
    </div>
  );
}
